"use client";

// Lucide: same set and paths as src/mos-shared/icons.js (user, creditCard, calendar, lock).
import { Calendar, CreditCard, Lock, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Inline PayPal checkout. Stays on our page:
 * 1) PayPal Card Fields (guest card, no PayPal login) with our own Pay button.
 * 2) Secondary PayPal button (popup) for buyers who prefer PayPal.
 * Fallback when card fields are not eligible: Smart Buttons with the
 * guest "Debit or Credit Card" funding source rendered inline.
 * Never auto-redirects. The order is created lazily (createOrder runs only
 * on card submit or PayPal click), so viewing checkout creates nothing.
 */

type CardFieldsState = {
  emittedBy?: string;
  fields?: Record<string, { isValid?: boolean; isFocused?: boolean; isEmpty?: boolean; isPotentiallyValid?: boolean }>;
};
const FIELD_KEYS: Record<string, string> = {
  name: "cardNameField",
  number: "cardNumberField",
  expiry: "cardExpiryField",
  cvv: "cardCvvField",
};
const FIELD_TITLES: Record<string, string> = {
  name: "Name on card",
  number: "Card number",
  expiry: "Expiry date",
  cvv: "Security code",
};

type CardField = { render: (el: HTMLElement) => Promise<void> };
type CardFieldsInstance = {
  isEligible: () => boolean;
  NameField: (o?: object) => CardField;
  NumberField: (o?: object) => CardField;
  ExpiryField: (o?: object) => CardField;
  CVVField: (o?: object) => CardField;
  submit: () => Promise<void>;
  getState?: () => Promise<unknown>;
};

/** Longest the card form may stay behind the skeleton before it is revealed anyway. */
const REVEAL_MAX_MS = 6000;
const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * CardFields render() resolves once each hosted iframe is inserted, not once it has painted:
 * the iframe documents then load, apply the creation-time style and paint one by one.
 * Wait for every slot's iframe load event (hooked before render), then for getState() (answered only after every child
 * frame has initialised), then two animation frames plus a short settle so all four inputs
 * reveal in the same frame, already styled.
 */
/** Resolves on the load event of the iframe PayPal inserts into slot. Call before render(). */
function watchFrameLoad(slot: HTMLElement): Promise<void> {
  return new Promise<void>((resolve) => {
    const seen = new WeakSet<HTMLIFrameElement>();
    const hook = () => {
      slot.querySelectorAll("iframe").forEach((f) => {
        if (seen.has(f)) return;
        seen.add(f);
        f.addEventListener("load", () => {
          mo.disconnect();
          resolve();
        }, { once: true });
      });
    };
    const mo = new MutationObserver(hook);
    mo.observe(slot, { childList: true, subtree: true });
    hook();
  });
}

async function waitCardFieldsPainted(loads: Promise<void>[], fields: CardFieldsInstance): Promise<void> {
  await Promise.all(loads);
  if (fields.getState) await fields.getState().catch(() => undefined);
  await nextFrame();
  await nextFrame();
  await sleep(120);
}
type ButtonsInstance = {
  isEligible?: () => boolean;
  render: (el: HTMLElement) => Promise<void>;
};
type PaypalV5 = {
  CardFields?: (opts: {
    createOrder: () => Promise<string> | string;
    onApprove: (data: { orderID?: string }) => Promise<void> | void;
    onError?: (err: unknown) => void;
    style?: Record<string, Record<string, string>>;
    inputEvents?: Record<string, (state: CardFieldsState) => void>;
  }) => CardFieldsInstance;
  Buttons?: (opts: {
    fundingSource?: string;
    style?: Record<string, string | number | boolean>;
    createOrder: () => Promise<string> | string;
    onApprove: (data: { orderID?: string }) => Promise<void> | void;
    onClick?: (data?: { fundingSource?: string }) => void;
    onCancel?: (data?: unknown) => void;
    onError?: (err: unknown) => void;
  }) => ButtonsInstance;
  FUNDING?: Record<string, string>;
};

declare global {
  interface Window {
    paypal?: PaypalV5;
  }
}

/** Longest the inline card skeleton stays up if the card form never signals it rendered. */
const CARD_FORM_MAX_MS = 15000;
/** Iframe growth (px) that means PayPal expanded its guest card form. */
const CARD_FORM_GROWTH_PX = 60;

/**
 * Test hook for the Smart Buttons fallback (sandbox always offers Card Fields).
 * Add ?ppfallback=1 on preview or localhost to force the guest "Debit or Credit Card" path.
 */
function forceButtonsFallback(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (!(host.startsWith("preview.") || host === "localhost" || host === "127.0.0.1")) return false;
  return new URLSearchParams(window.location.search).get("ppfallback") === "1";
}

/** Same skeleton as the Card Fields loader. */
function CardSkeleton({ label }: { label: string }) {
  return (
    <div className="uc2-pp-skeleton" aria-label={label} role="status" aria-busy="true">
      <div className="uc2-pp-head">
        <span className="uc2-pp-skel-bar is-title" />
        <span className="uc2-pp-skel-bar is-brands" />
      </div>
      <div className="uc2-pp-card uc2-pp-skel-group">
        <div className="uc2-pp-field"><span className="uc2-pp-skel-bar is-text" /></div>
        <div className="uc2-pp-field"><span className="uc2-pp-skel-bar is-text is-wide" /></div>
        <div className="uc2-pp-row">
          <div className="uc2-pp-field"><span className="uc2-pp-skel-bar is-text is-short" /></div>
          <div className="uc2-pp-field"><span className="uc2-pp-skel-bar is-text is-short" /></div>
        </div>
      </div>
      <div className="uc2-pp-skel-pay" />
    </div>
  );
}

/** One shared load per SDK URL. A remount while the SDK is still loading reuses it. */
let sdkLoad: { src: string; promise: Promise<void> } | null = null;
function loadScript(src: string): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[data-pp-sdk]`);
  if (existing && existing.src === src && window.paypal?.Buttons) return Promise.resolve();
  // Never pull a script that is still loading: a second SDK copy tears down the first
  // copy's components, which closes an open PayPal popup without an error.
  if (sdkLoad && sdkLoad.src === src) return sdkLoad.promise;
  if (existing) existing.remove();
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.ppSdk = "1";
    script.onload = () => resolve();
    script.onerror = () => {
      if (sdkLoad?.promise === promise) sdkLoad = null;
      script.remove();
      reject(new Error("PayPal could not load. Try again."));
    };
    document.head.appendChild(script);
  });
  sdkLoad = { src, promise };
  return promise;
}

/** Resolve theme tokens to concrete colors (iframes cannot see our CSS vars). */
function themeColors(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const text = cs.color || "inherit";
  const probe = document.createElement("span");
  probe.style.color = "var(--mos-muted, var(--mos-text))";
  el.appendChild(probe);
  const muted = getComputedStyle(probe).color || text;
  probe.style.color = "var(--danger, var(--mos-accent))";
  const danger = getComputedStyle(probe).color || text;
  probe.style.color = "var(--mos-plate, var(--mos-page))";
  const plate = getComputedStyle(probe).color || "transparent";
  probe.remove();
  const dark = document.documentElement.dataset.appearance === "dark";
  return { text, muted, danger, plate, font: cs.fontFamily, dark };
}

export function PaypalCheckoutButtons({
  clientId,
  currency = "USD",
  createOrder,
  onApprove,
  onCancel,
  onError,
  payLabel = "Pay",
}: {
  payLabel?: string;
  currency?: string;
  clientId: string;
  createOrder: () => Promise<string>;
  onApprove: (orderId?: string) => void | Promise<void>;
  /** Buyer closed the PayPal popup. */
  onCancel?: () => void;
  /** Raw SDK error object. The caller maps it to copy (or treats popup-closed as a cancel). */
  onError: (error: unknown) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const expiryRef = useRef<HTMLDivElement>(null);
  const cvvRef = useRef<HTMLDivElement>(null);
  const paypalRef = useRef<HTMLDivElement>(null);
  const cardButtonRef = useRef<HTMLDivElement>(null);
  const cardFieldsRef = useRef<CardFieldsInstance | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const [mode, setMode] = useState<"loading" | "fields" | "buttons" | "failed">("loading");
  const [ready, setReady] = useState(false);
  /** Guest card form (Smart Buttons fallback) is opening: skeleton below the card button. */
  const [cardPending, setCardPending] = useState(false);
  const cardPendingRef = useRef<(() => void) | null>(null);
  /**
   * Theme follow without losing typed data. PayPal has no API to restyle live fields, so:
   * the iframe inputs paint a transparent background and our token-styled wrapper
   * (.uc2-pp-card, --mos-plate) shows through and follows the theme at once. Text colours
   * live inside the iframes, so a theme change only marks them stale; they are re-created
   * when every field is empty, unfocused and nothing is paying (deferred until then, or
   * until checkout reopens). Typed card details are never wiped.
   */
  const [themeKey, setThemeKey] = useState(0);
  const [themeStale, setThemeStale] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    const sig = () => {
      const el = rootRef.current;
      if (!el) return "";
      const c = themeColors(el);
      return [c.text, c.muted, c.danger, c.font, c.dark].join("|");
    };
    let last = sig();
    const mo = new MutationObserver(() => {
      const next = sig();
      if (!next || next === last) return;
      last = next;
      setThemeStale(true);
    });
    mo.observe(root, { attributes: true, attributeFilter: ["class", "style", "data-appearance", "data-theme"] });
    return () => mo.disconnect();
  }, []);
  const [paying, setPaying] = useState(false);
  /** Per field: focused now, and invalid (shown only after blur). */
  const [fieldUi, setFieldUi] = useState<Record<string, { focus?: boolean; bad?: boolean; filled?: boolean }>>({});
  const untouched = !Object.values(fieldUi).some((f) => f?.filled || f?.focus);
  useEffect(() => {
    if (!themeStale || paying || !untouched) return;
    setThemeStale(false);
    setFieldUi({});
    setThemeKey((k) => k + 1);
  }, [themeStale, paying, untouched]);
  const onApproveRef = useRef(onApprove);
  const onErrorRef = useRef(onError);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const createOrderRef = useRef(createOrder);
  createOrderRef.current = createOrder;
  onApproveRef.current = onApprove;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    // After unmount (leaving Plans, theme re-init) the SDK tears its frames down and can emit
    // "Window closed before response" style errors; those are never shown.
    const fail = (err: unknown) => {
      if (cancelled) return;
      onErrorRef.current(err);
    };
    const cancel = () => {
      if (cancelled) return;
      onCancelRef.current?.();
    };
    const makeOrder = () => createOrderRef.current();
    const syncUi = (state: CardFieldsState, kind: "focus" | "blur" | "change") => {
      const fields = state.fields ?? {};
      setFieldUi((prev) => {
        const next = { ...prev };
        for (const [ui, key] of Object.entries(FIELD_KEYS)) {
          const f = fields[key];
          if (!f) continue;
          const wasBlurred = state.emittedBy === key && kind === "blur";
          const bad = wasBlurred
            ? !f.isEmpty && !f.isValid
            : prev[ui]?.bad && !f.isValid;
          next[ui] = { focus: Boolean(f.isFocused), bad: Boolean(bad), filled: !f.isEmpty };
        }
        return next;
      });
    };
    const titleIframes = () => {
      const pairs: Array<[HTMLDivElement | null, string]> = [
        [nameRef.current, FIELD_TITLES.name],
        [numberRef.current, FIELD_TITLES.number],
        [expiryRef.current, FIELD_TITLES.expiry],
        [cvvRef.current, FIELD_TITLES.cvv],
      ];
      for (const [el, title] of pairs) {
        el?.querySelectorAll("iframe").forEach((frame) => {
          void title;
          if (frame.getAttribute("title") !== "") frame.setAttribute("title", "");
          frame.removeAttribute("name");
        });
      }
    };
    async function mount(attempt = 0): Promise<void> {
      setReady(false);
      try {
        const cur = (currency || "USD").trim().toUpperCase() || "USD";
        await loadScript(
          `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${encodeURIComponent(cur)}&intent=capture&components=buttons,card-fields`,
        );
        const pp = window.paypal;
        if (cancelled || !pp || !rootRef.current) return;
        const colors = themeColors(rootRef.current);
        let fieldsOk = false;
        if (pp.CardFields && !forceButtonsFallback()) {
          // Every visual of the inputs is set here, at creation, so the iframes paint styled once.
          // PayPal style guide: only supported properties, passed at creation.
          const flat = { border: "none", outline: "none", "box-shadow": "none" };
          const cardStyle: Record<string, Record<string, string>> = {
            input: {
              ...flat,
              "font-size": "15px",
              "font-family": colors.font,
              color: colors.text,
              // Transparent so the themed wrapper (.uc2-pp-card) shows through on theme change.
              background: "transparent",
              "border-radius": "0",
              // Same indent as PayPal's own brand-logo rule (calc(40px + 1.2rem), !important in its
              // default sheet), so every field starts text at one x and the logo shares our icon slot.
              padding: "0 14px 0 calc(40px + 1.2rem)",
              height: "46px",
              "line-height": "normal",
            },
            "input::placeholder": { color: colors.muted },
            ":focus": { ...flat, color: colors.text },
            ".valid": { ...flat, color: colors.text },
            ".invalid": { ...flat, color: colors.danger },
          };
          const numberStyle = cardStyle;
          const fields = pp.CardFields({
            createOrder: makeOrder,
            onApprove: async (data) => {
              await onApproveRef.current(data?.orderID);
            },
            onError: (err) => {
              setPaying(false);
              fail(err);
            },
            inputEvents: {
              onFocus: (state) => syncUi(state, "focus"),
              onBlur: (state) => syncUi(state, "blur"),
              onChange: (state) => syncUi(state, "change"),
            },
            style: cardStyle,
          });
          if (fields.isEligible()) {
            setMode("fields");
            await new Promise((r) => requestAnimationFrame(() => r(null)));
            if (cancelled) return;
            cardFieldsRef.current = fields;
            const slots = [nameRef.current, numberRef.current, expiryRef.current, cvvRef.current].filter(
              (el): el is HTMLDivElement => !!el,
            );
            const loads = slots.map(watchFrameLoad);
            await Promise.all([
              nameRef.current && fields.NameField({ placeholder: "Name on card" }).render(nameRef.current),
              numberRef.current && fields.NumberField({ placeholder: "Card number", style: numberStyle }).render(numberRef.current),
              expiryRef.current && fields.ExpiryField({ placeholder: "MM / YY" }).render(expiryRef.current),
              cvvRef.current && fields.CVVField({ placeholder: "CVV" }).render(cvvRef.current),
            ]);
            await Promise.race([waitCardFieldsPainted(loads, fields), sleep(REVEAL_MAX_MS)]);
            if (cancelled) return;
            titleIframes();
            setReady(true);
            if (rootRef.current) {
              const mo = new MutationObserver(() => titleIframes());
              mo.observe(rootRef.current, { subtree: true, childList: true, attributes: true, attributeFilter: ["title", "name"] });
              observerRef.current = mo;
            }
            fieldsOk = true;
          }
        }
        if (!fieldsOk) {
          setMode("buttons");
          await new Promise((r) => requestAnimationFrame(() => r(null)));
          if (cancelled || !pp.Buttons || !cardButtonRef.current) return;
          const host = cardButtonRef.current;
          // Skeleton from click until PayPal's iframe grows (card form expanded) after
          // createOrder resolved, or until cancel/error, or a timeout fallback.
          let ro: ResizeObserver | null = null;
          let timer: ReturnType<typeof setTimeout> | null = null;
          const stopPending = () => {
            ro?.disconnect();
            ro = null;
            if (timer) clearTimeout(timer);
            timer = null;
            cardPendingRef.current = null;
            if (!cancelled) setCardPending(false);
          };
          const startPending = () => {
            stopPending();
            const base = host.getBoundingClientRect().height;
            let orderDone = false;
            let grown = false;
            const check = () => {
              if (orderDone && grown) stopPending();
            };
            ro = new ResizeObserver(() => {
              if (host.getBoundingClientRect().height - base >= CARD_FORM_GROWTH_PX) {
                grown = true;
                check();
              }
            });
            ro.observe(host);
            timer = setTimeout(stopPending, CARD_FORM_MAX_MS);
            cardPendingRef.current = () => {
              orderDone = true;
              check();
            };
            setCardPending(true);
          };
          const card = pp.Buttons({
            fundingSource: pp.FUNDING?.CARD ?? "card",
            style: { shape: "pill", height: 46 },
            onClick: (data) => {
              if (!data?.fundingSource || data.fundingSource === (pp.FUNDING?.CARD ?? "card")) startPending();
            },
            createOrder: async () => {
              try {
                const id = await makeOrder();
                cardPendingRef.current?.();
                return id;
              } catch (err) {
                stopPending();
                throw err;
              }
            },
            onApprove: (data) => {
              stopPending();
              return onApproveRef.current(data?.orderID);
            },
            onCancel: () => {
              stopPending();
              cancel();
            },
            onError: (err) => {
              stopPending();
              fail(err);
            },
          });
          if (!card.isEligible || card.isEligible()) await card.render(cardButtonRef.current);
        }
        if (cancelled || !pp.Buttons || !paypalRef.current) return;
        const paypalButton = pp.Buttons({
          fundingSource: pp.FUNDING?.PAYPAL ?? "paypal",
          style: { shape: "pill", height: 46, color: colors.dark ? "black" : "white", label: "paypal", tagline: false },
          createOrder: makeOrder,
          onApprove: (data) => onApproveRef.current(data?.orderID),
          onCancel: cancel,
          onError: fail,
        });
        await paypalButton.render(paypalRef.current);
      } catch (err) {
        if (cancelled) return;
        if (attempt < 2) {
          // Transient SDK/render races (a container re-rendering mid-render): retry quietly.
          observerRef.current?.disconnect();
          observerRef.current = null;
          cardFieldsRef.current = null;
          for (const r of [nameRef, numberRef, expiryRef, cvvRef, paypalRef, cardButtonRef]) {
            if (r.current) r.current.innerHTML = "";
          }
          setReady(false);
          setMode("loading");
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
          if (!cancelled) return mount(attempt + 1);
          return;
        }
        // Shown inline below ("PayPal could not load"); no toast while browsing.
        setMode("failed");
      }
    }
    void mount();
    return () => {
      cancelled = true;
      cardFieldsRef.current = null;
      cardPendingRef.current = null;
      setCardPending(false);
      observerRef.current?.disconnect();
      observerRef.current = null;
      for (const r of [nameRef, numberRef, expiryRef, cvvRef, paypalRef, cardButtonRef]) {
        if (r.current) r.current.innerHTML = "";
      }
    };
  }, [clientId, currency, themeKey]);

  const submitCard = async () => {
    const fields = cardFieldsRef.current;
    if (!fields || paying) return;
    setPaying(true);
    try {
      await fields.submit();
    } catch (err) {
      onErrorRef.current(err instanceof Error && err.message ? err : new Error("Check your card details and try again."));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="studio-paypal-buttons uc2-pp" ref={rootRef}>
      {mode === "loading" || (mode === "fields" && !ready) ? (
        <CardSkeleton label="Loading secure card form" />
      ) : null}
      {/* One <form> around all four PayPal frames: Chrome groups frame fields into one card
          form only when they descend from the same form, so autofill started in any PayPal
          field can fill the sibling PayPal fields (same origin). No card data touches our page. */}
      <form
        className={`uc2-pp-fields${ready ? " is-ready" : " is-pending"}`}
        hidden={mode !== "fields"}
        autoComplete="on"
        aria-label="Card details"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submitCard();
        }}
      >
        <div className="uc2-pp-head">
          <span className="uc2-pp-title">
            <Lock size={12} strokeWidth={2.25} aria-hidden="true" />
            Card details
          </span>
          <span className="uc2-pp-brands" aria-label="Visa, Mastercard, American Express">
            <img className="uc2-pp-brand" src="/branding/cards/visa.svg" alt="Visa" width={24} height={16} />
            <img className="uc2-pp-brand" src="/branding/cards/mastercard.svg" alt="Mastercard" width={24} height={16} />
            <img className="uc2-pp-brand" src="/branding/cards/amex.svg" alt="American Express" width={24} height={16} />
          </span>
        </div>
        <div className="uc2-pp-card">
          <div className={`uc2-pp-field${fieldUi.name?.focus ? " is-focused" : ""}${fieldUi.name?.bad ? " is-invalid" : ""}`}>
            <span className="uc2-pp-icon" aria-hidden="true"><User size={16} strokeWidth={2} /></span>
            <div className="uc2-pp-slot" ref={nameRef} />
          </div>
          <div className={`uc2-pp-field is-number${fieldUi.number?.filled ? " has-brand" : ""}${fieldUi.number?.focus ? " is-focused" : ""}${fieldUi.number?.bad ? " is-invalid" : ""}`}>
            <div className="uc2-pp-slot" ref={numberRef} />
            {/* Plate over PayPal's own brand graphic (left of the iframe); no style option hides it. */}
            <span className="uc2-pp-brand-cover" aria-hidden="true">
              <span className="uc2-pp-icon"><CreditCard size={16} strokeWidth={2} /></span>
            </span>
          </div>
          <div className="uc2-pp-row">
            <div className={`uc2-pp-field${fieldUi.expiry?.focus ? " is-focused" : ""}${fieldUi.expiry?.bad ? " is-invalid" : ""}`}>
            <span className="uc2-pp-icon" aria-hidden="true"><Calendar size={16} strokeWidth={2} /></span>
            <div className="uc2-pp-slot" ref={expiryRef} />
          </div>
            <div className={`uc2-pp-field${fieldUi.cvv?.focus ? " is-focused" : ""}${fieldUi.cvv?.bad ? " is-invalid" : ""}`}>
            <span className="uc2-pp-icon" aria-hidden="true"><Lock size={16} strokeWidth={2} /></span>
            <div className="uc2-pp-slot" ref={cvvRef} />
          </div>
          </div>
        </div>
        <button
          type="button"
          className="uc2-pay uc2-pp-pay"
          data-no-press=""
          disabled={paying}
          aria-busy={paying}
          onClick={() => void submitCard()}
        >
          <Lock size={15} strokeWidth={2.25} aria-hidden="true" />
          <span>{paying ? "Processing" : payLabel}</span>
        </button>
        <p className="uc2-pp-secure">
          <Lock size={11} strokeWidth={2.25} aria-hidden="true" />
          Payments are encrypted and processed securely by PayPal.
        </p>
      </form>
      <div ref={cardButtonRef} hidden={mode !== "buttons"} />
      {mode === "buttons" && cardPending ? <CardSkeleton label="Loading card form" /> : null}
      {mode === "fields" || mode === "buttons" ? <p className="uc2-pp-or">or</p> : null}
      <div className="uc2-pp-paypal" ref={paypalRef} hidden={mode === "failed" || mode === "loading"} />
      {mode === "failed" ? (
        <p className="uc2-pp-loading">PayPal could not load. Reload to try again.</p>
      ) : null}
    </div>
  );
}

/** Signed-out demo: static look-alike of the inline card form. Loads nothing, sends nothing. */
export function PaypalCardPreview({ payLabel = "Pay", onPay }: { payLabel?: string; onPay?: () => void }) {
  const field = (Icon: typeof Lock, placeholder: string, autoComplete: string) => (
    <div className="uc2-pp-field">
      <span className="uc2-pp-icon" aria-hidden="true"><Icon size={16} strokeWidth={2} /></span>
      <input className="uc2-pp-fake" placeholder={placeholder} autoComplete={autoComplete} aria-label={placeholder} />
    </div>
  );
  return (
    <div className="studio-paypal-buttons uc2-pp is-preview">
      <div className="uc2-pp-fields">
        <div className="uc2-pp-head">
          <span className="uc2-pp-title">
            <Lock size={12} strokeWidth={2.25} aria-hidden="true" />
            Card details
          </span>
          <span className="uc2-pp-brands" aria-label="Visa, Mastercard, American Express">
            <img className="uc2-pp-brand" src="/branding/cards/visa.svg" alt="Visa" width={24} height={16} />
            <img className="uc2-pp-brand" src="/branding/cards/mastercard.svg" alt="Mastercard" width={24} height={16} />
            <img className="uc2-pp-brand" src="/branding/cards/amex.svg" alt="American Express" width={24} height={16} />
          </span>
        </div>
        <div className="uc2-pp-card">
          {field(User, "Name on card", "cc-name")}
          {field(CreditCard, "Card number", "cc-number")}
          <div className="uc2-pp-row">
            {field(Calendar, "MM / YY", "cc-exp")}
            {field(Lock, "CVV", "cc-csc")}
          </div>
        </div>
        <button type="button" className="uc2-pay uc2-pp-pay" data-no-press="" onClick={onPay}>
          <Lock size={15} strokeWidth={2.25} aria-hidden="true" />
          <span>{payLabel}</span>
        </button>
        <p className="uc2-pp-secure">
          <Lock size={11} strokeWidth={2.25} aria-hidden="true" />
          Payments are encrypted and processed securely by PayPal.
        </p>
      </div>
      <p className="uc2-pp-or">or</p>
      <div className="uc2-pp-paypal-fake" aria-hidden="true">
        <img src="/branding/paypal.svg" alt="" height={18} />
      </div>
    </div>
  );
}
